// src/components/OnboardingUploadRedirect.jsx
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const OnboardingUploadRedirect = () => {
  const [searchParams] = useSearchParams();
  const recordId = searchParams.get('recordId');
  const [isLoading, setIsLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  const salesforceUploadUrl = recordId 
    ? `https://cerebralstaffing.my.salesforce-sites.com/uploadvf?recordId=${recordId}`
    : null;

  useEffect(() => {
    if (!recordId) {
      // If no recordId is provided, redirect to contact page as a fallback
      window.location.href = '/contact';
      return;
    }

    // Set a timeout to handle loading state
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [recordId]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setIframeError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setIframeError(true);
  };

  const handleFallbackRedirect = () => {
    if (salesforceUploadUrl) {
      window.open(salesforceUploadUrl, '_blank');
    }
  };

  if (!recordId) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        fontFamily: 'Arial, sans-serif'
      }}>
        <h2>Missing Record ID</h2>
        <p>No record ID was provided. Redirecting to contact page...</p>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%',
      height: '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Loading Overlay */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          zIndex: 1000,
          fontFamily: 'Arial, sans-serif'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#28346c' }}>
            Loading Onboarding Upload Portal...
          </h2>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #28346c',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        </div>
      )}

      {/* Error Fallback */}
      {iframeError && !isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          zIndex: 1000,
          fontFamily: 'Arial, sans-serif',
          padding: '20px'
        }}>
          <h2 style={{ color: '#28346c', marginBottom: '10px' }}>
            Unable to Load Upload Portal
          </h2>
          <p style={{ marginBottom: '20px', textAlign: 'center' }}>
            The upload portal cannot be displayed directly. Click below to open it in a new window.
          </p>
          <button
            onClick={handleFallbackRedirect}
            style={{
              backgroundColor: '#28346c',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Open Upload Portal
          </button>
        </div>
      )}

      {/* Iframe */}
      {salesforceUploadUrl && !iframeError && (
        <iframe
          src={salesforceUploadUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: isLoading ? 'none' : 'block'
          }}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          title="Onboarding Upload Portal"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-downloads"
        />
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default OnboardingUploadRedirect;